<template>
  <FairSurveyDialog
    v-model:open="visible"
    :title="configTitle"
    :show-title="isWelcomeStep"
    :exit-label="isWelcomeStep ? 'Назад' : 'Сохранить и выйти'"
    :show-progress="showProgress"
    :progress-percent="progressPercentage"
    :progress-label="progressLabel"
    :loading="giftModal.isLoadingConfig"
    loading-label="Загрузка опроса…"
    :show-footer="showFooter"
    :show-saved="showSavedIndicator"
    saved-label="Ответы сохранены"
    :compact="!isDesktop"
    @close="handleHideGiftModal"
  >
    <DynamicStepRenderer />

    <template #actions>
      <FairButton
        v-if="showBackButton"
        label="Назад"
        variant="tonal"
        size="m"
        :rounded="false"
        @click="prevStep"
      />
      <FairButton
        v-if="showNextButton"
        :label="nextButtonLabel"
        variant="primary"
        size="m"
        :rounded="false"
        :disabled="giftModal.isLoading"
        @click="handleNext"
      />
    </template>
  </FairSurveyDialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { FairSurveyDialog } from "@/ui/patterns/FairSurveyDialog";
import { FairButton } from "@/ui/primitives/FairButton";

import { useGiftModal } from "@/features/gift/store";
import DynamicStepRenderer from "./GiftSteps/DynamicStepRenderer.vue";
import { useDevice } from "@/composables/useDevice";

const giftStore = useGiftModal();
const giftModal = computed(() => giftStore.giftModal);
const currentStep = computed(() => giftStore.currentStep);
const totalSteps = computed(() => giftStore.totalSteps);
const { isDevice } = useDevice()
const deviceType = computed(() => isDevice.value)
const isDesktop = computed(() => deviceType.value === 'desktop' || deviceType.value === 'smallDesktop')

const visible = ref(false);
const savedSteps = ref<Set<string>>(new Set());
const previousStepId = ref<string | null>(null);

const configTitle = computed(
  () => giftStore.giftModal.config?.title || "Подбор подарка"
);

const isWelcomeStep = computed(() => currentStep.value?.type === "welcome");

/** Welcome has nothing to measure yet; completion is past the last step. */
const showProgress = computed(() => {
  const stepType = currentStep.value?.type;
  return (
    Boolean(stepType) && stepType !== "welcome" && stepType !== "completion"
  );
});

const progressLabel = computed(
  () => `${giftModal.value.currentStepIndex + 1} из ${totalSteps.value}`
);

const progressPercentage = computed(() => {
  if (totalSteps.value === 0) return 0;
  return ((giftModal.value.currentStepIndex + 1) / totalSteps.value) * 100;
});

const showBackButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  const isFirstStep = giftModal.value.currentStepIndex === 0;
  
  // Hide back button on special steps
  if (stepType === 'completion') {
    return false;
  }
  
  return !isFirstStep;
});

const showNextButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  const isLastStep = giftModal.value.currentStepIndex >= totalSteps.value - 1;
  
  // Hide next button on special steps
  if (stepType === 'completion') {
    return false;
  }
  
  return !isLastStep;
});

const nextButtonLabel = computed(() => {
  if (!currentStep.value) return "Продолжить";
  
  if (currentStep.value.type === 'welcome') return "Начать тест";
  
  return "Продолжить";
});

const showFooter = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  // Hide footer on special steps
  return stepType !== 'completion';
});

const showSavedIndicator = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  const stepId = currentStep.value.id;
  
  // Не показываем плашку для исключенных шагов
  if (stepType === "welcome" || stepType === "completion") {
    return false;
  }
  
  // Показываем если есть данные для текущего шага и шаг в списке сохраненных
  const hasData = giftStore.giftModal.giftData[stepId] !== undefined && 
                  giftStore.giftModal.giftData[stepId] !== null &&
                  (Array.isArray(giftStore.giftModal.giftData[stepId]) 
                    ? giftStore.giftModal.giftData[stepId].length > 0
                    : true);
  
  return hasData && savedSteps.value.has(stepId);
});

function handleHideGiftModal() {
  giftStore.hideGiftModal();
}

function prevStep() {
  giftStore.prevStep();
}

function handleNext() {
  if (!currentStep.value) return;
  
  giftStore.nextStep();
}

watch(
  () => giftModal.value.isShow,
  (newVal) => {
    visible.value = newVal;
    if (!newVal) {
      // Сбрасываем состояние при закрытии модального окна
      savedSteps.value.clear();
      previousStepId.value = null;
    }
  }
);

// Отслеживаем изменения в giftData и добавляем ID шага в список сохраненных
watch(
  () => giftStore.giftModal.giftData,
  (newData) => {
    if (!currentStep.value) return;
    const stepId = currentStep.value.id;
    const stepType = currentStep.value.type;
    
    // Не отслеживаем исключенные шаги
    if (stepType === "welcome" || stepType === "completion") {
      return;
    }
    
    // Проверяем, есть ли данные для текущего шага
    const hasData = newData[stepId] !== undefined && 
                    newData[stepId] !== null &&
                    (Array.isArray(newData[stepId]) 
                      ? newData[stepId].length > 0
                      : true);
    
    if (hasData) {
      savedSteps.value.add(stepId);
    }
  },
  { deep: true }
);

// Отслеживаем смену шага и проверяем, есть ли уже данные для нового шага
watch(
  () => currentStep.value?.id,
  (newStepId) => {
    if (!newStepId || !currentStep.value) return;
    
    const stepType = currentStep.value.type;
    
    // Не обрабатываем исключенные шаги
    if (stepType === "welcome" || stepType === "completion") {
      previousStepId.value = newStepId;
      return;
    }
    
    // Проверяем, есть ли уже данные для этого шага
    const stepData = giftStore.giftModal.giftData[newStepId];
    const hasData = stepData !== undefined && 
                    stepData !== null &&
                    (Array.isArray(stepData) ? stepData.length > 0 : true);
    
    if (hasData) {
      savedSteps.value.add(newStepId);
    }
    
    previousStepId.value = newStepId;
  }
);
</script>
